import css from "./homeinfo.scss?inline";
import globalStyles from "../../index.scss?inline";
import { Component } from "../../utils/Component";
import { ArgSpecDictionary } from "../component-utils";

const DEBUG_VERBOSE: boolean = false;
const CLASS_NAME: string = "HHDSHomeInfo";
export const HHDSHomeInfoTagName: string = "hhds-home-info";
const TAG_NAME: string = HHDSHomeInfoTagName;

export const HHDSHomeInfoAttrNames = {
	beds: "beds",
	baths: "baths",
	garage: "garage",
	story: "story",
	sqft: "sqft",
	price: "price",
	type: "type",
	size: "size",
};

export enum HHDSHomeInfoType {
	plan = "plan",
	lot = "lot",
	collection = "collection",
}

export enum HHDSHomeInfoSize {
	small = "small",
	large = "large",
}

const Attrs = HHDSHomeInfoAttrNames;

export class HHDSHomeInfo extends Component {
	private _size: HHDSHomeInfoSize = HHDSHomeInfoSize.small;
	private labelClass: string = "";
	private valueClass: string = "";

	constructor() {
		super();
		// The base class's constructor handles attachmennt of a shadow root and
		// adopted global styles. Access the shadow root via this.shadow.
		//
		// Use the constructor only for anything that will never need to be destroyed as part of the
		// component's update lifecycle. init() and destroy() are called for connectedCallback and
		// disconnectedCallback, and a destroy() init() pair is called if reinit() is utilised.
	}

	protected override init(): void {
		// The base class responds to connectedCallback() by collecting attributes into
		// this.vars, then calling init(). A call to super.init() is not required.
		DEBUG_VERBOSE && console.log(CLASS_NAME, "init");

		const type = this.vars.get<number>(Attrs.type);
		// console.log(`${Attrs.myNumericArg}: ${argValue} (type: ${typeof argValue})`);

		this._size = this.vars.get<HHDSHomeInfoSize>(Attrs.size);

		this.labelClass =
			this._size == HHDSHomeInfoSize.large
				? `${TAG_NAME}__label uppercase uppercase--01`
				: "eyebrow eyebrow--01";
		this.valueClass =
			this._size == HHDSHomeInfoSize.large
				? "uppercase uppercase--01"
				: "bodytext bodytext--01";

		if (this._size == HHDSHomeInfoSize.large) {
			this._size = HHDSHomeInfoSize.small;
		}

		// For some components, re-assigning innerHTML may be appropriate on attribute and slot changes,
		// and without the need for a reinit(). In other cases, assigning innerHTML explicitly as part of
		// the init() routine is more appropriate.

		this.shadow.innerHTML = `
			<style>${globalStyles}</style>
			<style>${css}</style>
			<div class="${TAG_NAME} ${TAG_NAME}--${type}">
        <hhds-richtext>
          <table cellspacing="0">
            <tr>
              <td>
                <p class="${this.labelClass}">BEDS</p>
                <p class="${this.valueClass}">${this.formatInfo(
			this.vars.get<number>(Attrs.beds)
		)}</p>
              </td>
              <td>
                <p class="${
									type == "collections" || type == "plans"
										? `${TAG_NAME}__label uppercase uppercase--01`
										: "eyebrow eyebrow--01"
								}">BATHS</p>
                <p class="${
									type == "collections" || type == "plans"
										? "uppercase uppercase--01"
										: "bodytext bodytext--01"
								}">${this.formatInfo(this.vars.get<number>(Attrs.baths))}</p>
              </td>
              <td>
                <p class="${
									type == "collections" || type == "plans"
										? `${TAG_NAME}__label uppercase uppercase--01`
										: "eyebrow eyebrow--01"
								}">GARAGE</p>
                <p class="${
									type == "collections" || type == "plans"
										? "uppercase uppercase--01"
										: "bodytext bodytext--01"
								}">${this.formatInfo(this.vars.get<number>(Attrs.garage))}</p>
              </td>
              <td>
                <p class="${
									type == "collections" || type == "plans"
										? `${TAG_NAME}__label uppercase uppercase--01`
										: "eyebrow eyebrow--01"
								}">STORY</p>
                <p class="${
									type == "collections" || type == "plans"
										? "uppercase uppercase--01"
										: "bodytext bodytext--01"
								}">${this.formatInfo(this.vars.get<number>(Attrs.story))}</p>
              </td>
            </tr>
            <tr>
              <td colspan="2">
                <p class="${
									type == "collections" || type == "plans"
										? `${TAG_NAME}__label uppercase uppercase--01`
										: "eyebrow eyebrow--01"
								}">SQ. FT.</p>
                <p class="${
									type == "collections" || type == "plans"
										? "uppercase uppercase--01"
										: "bodytext bodytext--01"
								}">${this.formatSqFt(this.vars.get<number>(Attrs.sqft))}</p>
              </td>
              <td colspan="2">${this.getPriceHTML(type)}</td>
            </tr>
          </table>
        </hhds-richtext>
      </div>`;

		// If the component uses slots, use observeSlotChanges().
		// this.observeSlotChanges(true);
	}

	private getPriceHTML(type: string): string {
		const price = this.vars.get<number>(Attrs.price);

		if (type == "plan" || type == "collection") {
			return `<p class="${this.labelClass}">FROM</p><p class="${
				this.valueClass
			}">${price ? this.formatPrice(price) : "TBD"}</p>`;
		} else if (type == "lot") {
			return `<p class="${this.labelClass}">PRICE</p><p class="${
				this.valueClass
			}">${
				price ? this.formatPrice(price) : "TBD"

				// return `<p class="eyebrow eyebrow--01">PRICE</p><p class="bodytext bodytext--01">${
				// 	price ? this.formatPrice(price) : "TBD"
			}</p>`;
		} else {
			return "";
		}
	}

	private formatInfo(info: any): string {
		// Check if this is a range
		if (info.indexOf("-") > -1) {
			const range = info.split("-");
			return `${range[0].toLocaleString()} - ${range[1].toLocaleString()}`;
		}

		return info;
	}

	private formatPrice(price: any): string {
		const parsed = parseInt(price.toString().replace(/,/g, ""));
		if (!isNaN(parsed)) {
			return `$${parsed.toLocaleString()}`;
		} else {
			return price;
		}
	}

	private formatSqFt(sqft: any): string {
		if (sqft.indexOf("-") > -1) {
			const range = sqft.split("-");
			const min = parseInt(range[0].toString().replace(/,/g, ""));
			const max = parseInt(range[1].toString().replace(/,/g, ""));
			return `${min.toLocaleString()} - ${max.toLocaleString()}`;
		} else {
			const parsed = parseInt(sqft.toString().replace(/,/g, ""));
			if (!isNaN(parsed)) {
				return `${parsed.toLocaleString()}`;
			} else {
				return sqft;
			}
		}
	}

	protected override destroy(): void {
		// The base class responds to disconnectedCallback() by collecting attributes into
		// this.vars, then calling destroy(). A call to super.destroy() is not required.
		DEBUG_VERBOSE && console.log(CLASS_NAME, "destroy");
		// If the component uses slots, stop observing slot changes.
		// this.observeSlotChanges(false);
	}

	override onAttributeChanged(
		name: string,
		_oldValue: string,
		newValue: string
	): void {
		DEBUG_VERBOSE &&
			console.log(CLASS_NAME, "Attribute changed: ", name, _oldValue, newValue);
		// Either call reinit() to have the component's destroy and init methods each be called,
		// or skip this step and handle update of the attribute directly. 'this.vars' will already
		// have been updated by the base Component class, so it can be immediately used to access
		// the new value.
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, elements: Element[]): void {
		if (elements.length == 0) {
			DEBUG_VERBOSE && console.log(CLASS_NAME, "Slot emptied");
		} else {
			DEBUG_VERBOSE && console.log(CLASS_NAME, "Slot changed");
		}
	}

	static override argSpecs(): ArgSpecDictionary {
		// The base Component class must have access to this superclass's ArgSpecs.
		return ArgSpecs;
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	/*my-enum: {
    description: "Some enum-based argument.",
    defaultValue: MyEnum.myValue,
    typeString: "MyEnum",
    type: MyEnum,
  },*/
	[Attrs.type]: {
		description: "Type fo home info: 'Default', 'Collections'",
		defaultValue: HHDSHomeInfoType.plan,
		type: HHDSHomeInfoType,
	},
	[Attrs.size]: {
		description: "Size of the home info: 'small', 'large",
		defaultValue: HHDSHomeInfoSize.small,
		type: HHDSHomeInfoSize,
	},

	[Attrs.beds]: {
		description: "Number of bedrooms (eg 1-2)",
		defaultValue: "",
		type: String,
	},
	[Attrs.baths]: {
		description: "Number of bathrooms (eg 1-2)",
		defaultValue: "",
		type: String,
	},
	[Attrs.garage]: {
		description: "Number of garages (eg 1-2)",
		defaultValue: "",
		type: String,
	},
	[Attrs.story]: {
		description: "Number of stories (eg 1-2)",
		defaultValue: "",
		type: String,
	},
	[Attrs.sqft]: {
		description: "Square footage of property (eg 10,000-20,000)",
		defaultValue: "",
		type: String,
	},
	[Attrs.price]: {
		description: "Price of Property (eg $500,000)",
		defaultValue: "",
		type: String,
	},
};
